import { oc } from "@orpc/contract";
import { z } from "zod";
import { SD_849_RECORD_UNPLACED_REASON_VALUES } from "@erp/shared-types";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();

/**
 * EDI849 Response Status enum
 */
export const EDI849LineResultsStatusSchema = z.enum([
  "accepted",
  "rejected",
  "partial",
  "unknown",
  "resubmitted",
  /**
   * The email carried NO accept/reject decision but did say something the team
   * must act on (a request for a correct part number, a flagged SPA overlap) —
   * captured as an `advisories` entry. Distinct from `unknown`, which means "we
   * could not read a decision out of this": an advisory reads as a supplier
   * question awaiting our reply, not as a broken parse.
   */
  "advisory",
]);
export type EDI849LineResultsStatus = z.infer<
  typeof EDI849LineResultsStatusSchema
>;

/**
 * What WE are doing about this claim — user-controlled, one per 849 response.
 *
 * Distinct from {@link EDI849LineResultsStatusSchema}, which reports what the
 * supplier said about the lines we submitted. Nullable on the response: an 849
 * nobody has triaged carries no status at all, rather than a manufactured one.
 *
 * Keep in sync with the `SD849Status` model enum.
 */
export const SD849StatusSchema = z.enum([
  "needs_review",
  "waiting_on_supplier",
  "resubmitted",
  "needs_more_data",
  "complete",
  "closed",
]);
export type SD849Status = z.infer<typeof SD849StatusSchema>;

/**
 * The claim-status filter on the responses list.
 *
 * `"none"` is not a status — it selects the rows that have none, which is most
 * of the history: existing 849s were deliberately left un-backfilled (see
 * {@link SD849StatusSchema}), so without this option the queue ops most needs
 * to work through is the one it cannot ask for.
 */
export const SD849_STATUS_FILTER_NONE = "none";
export const SD849StatusFilterSchema = z.enum([
  ...SD849StatusSchema.options,
  SD849_STATUS_FILTER_NONE,
]);
export type SD849StatusFilter = z.infer<typeof SD849StatusFilterSchema>;

/**
 * Pagination schema
 */
export const EDI849PaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

/**
 * Email summary for response
 */
const ResponseEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string().nullable().optional(),
  from: z.string().nullable().optional(),
  /**
   * When the SUPPLIER's email arrived — null when we don't know.
   *
   * Nullable rather than falling back to the email row's `created_at`, which is
   * what this used to do: that substitution reports our ingestion time as the
   * supplier's send time, and a UI cannot tell the two apart to say so. An
   * unknown receipt time renders as a dash.
   */
  receivedAt: z.string().nullable(),
});

/**
 * One email on the claim, and what it is DOING there (#7182).
 *
 * `decision` is an email this 849's verdicts came out of — its anchor, or one folded
 * in by the lines attach. `context` is an email linked as evidence, from which no
 * verdict was ever read: a chase, a correction, the supplier explaining why a line is
 * held. Both belong in front of the analyst, and telling them apart is the whole
 * point of showing them together — a context email that reads as a decision invites
 * the question "why did this change nothing?".
 */
const ClaimEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string().nullable(),
  from: z.string().nullable(),
  receivedAt: z.string().nullable(),
  role: z.enum(["decision", "context"]),
  /** The response's source email — the one `email` names (its first decision email). */
  isAnchor: z.boolean(),
});
export type ClaimEmail = z.infer<typeof ClaimEmailSchema>;

/** A report row as the supplier sent it, shown with the row it belongs to (SD-2). */
const EDI849ReportedRowSchema = z.object({
  status: z.string(),
  matchFields: z.record(z.string(), z.unknown()),
  rawData: z.record(z.string(), z.unknown()).optional(),
  columnOrder: z.array(z.string()).optional(),
  sourceSheet: z.string().optional(),
});

/** One of our lines a single supplier row answers along with others (SD-87). */
const EDI849LumpLineSchema = z.object({
  sdE844dId: z.string(),
  claimed: z.number(),
});

/**
 * Line result from 849 response (individual transaction)
 */
export const EDI849LineResultSchema = z.object({
  status: z.string(),
  matchFields: z.record(z.string(), z.unknown()),
  rawData: z.record(z.string(), z.unknown()).optional(),
  columnOrder: z.array(z.string()).optional(),
  rejectionCode: z.string().optional(),
  rejectionReason: z.string().optional(),
  /**
   * Rejection detail (SD-6, ADR 0012): the supplier's own reason for the line,
   * verbatim, from a Context email — shown beside the 849's rejection code and
   * labelled as coming from that email. Never a decision.
   */
  rejectionDetail: z
    .object({
      text: z.string(),
      sourceInboundEmailId: z.string(),
      receivedAt: z.string().nullable(),
    })
    .optional(),
  /**
   * The email 849's verdict this EDI line overruled (SD-94, ADR 0013): set only
   * when the two disagreed. The EDI decides; this is shown beside it.
   */
  displacedEmailVerdict: z
    .object({
      sd849ResponseId: z.string(),
      status: z.string(),
      acceptedAmount: z.string().nullable(),
    })
    .optional(),
  /**
   * What a later email said about this EDI line, when it disagreed (SD-94, ADR
   * 0013). The email never decides; this is shown beside the EDI's verdict.
   */
  laterEmailVerdict: z
    .object({
      inboundEmailId: z.string(),
      receivedAt: z.string().nullable(),
      status: z.string(),
      acceptedAmount: z.string().nullable(),
    })
    .optional(),
  gentlyRejectionCode: z.string().optional(),
  /**
   * Whether this 849 line was confidently tied back to an 844 we sent:
   * "matched" (exactly one sent line), "ambiguous" (multiple — not applied), or
   * "unmatched" (none / too few identity fields).
   * Absent on older responses parsed before this field existed.
   */
  matchStatus: z.enum(["matched", "ambiguous", "unmatched"]).optional(),
  /** The sd_e844d id this line tied to (when matched) */
  matchedSdE844dId: z.string().optional(),
  /** The worksheet the parser read this row from; absent on older responses. */
  sourceSheet: z.string().optional(),
  /**
   * Rows on another tab of the report that restate this one — duplicate rows,
   * shown with it and never counted on their own (SD-2).
   */
  duplicateRows: z.array(EDI849ReportedRowSchema).optional(),
  /** The supplier's rows this combined row stands for (SD-2). */
  combinedFrom: z
    .array(
      EDI849ReportedRowSchema.extend({
        duplicateRows: z.array(EDI849ReportedRowSchema).optional(),
      }),
    )
    .optional(),
  /**
   * A combined row split from ONE supplier row (SD-87): every line that row
   * answers, with the credit each was given.
   */
  splitAcross: z
    .array(EDI849LumpLineSchema.extend({ credited: z.number() }))
    .optional(),
  /**
   * A supplier row answering several of our lines and not paid as claimed, so
   * left unsplit (SD-87): the lines it covers.
   */
  coversLines: z.array(EDI849LumpLineSchema).optional(),
  /**
   * Placed through this alternate key column (the sent 844 line's), not the
   * row's own key — e.g. the supplier reported our product code (SD-3).
   */
  matchedOnAlternate: z
    .object({ field: z.string(), column: z.string() })
    .optional(),
  /**
   * Placed on a prefix of this key field: the row's code and the sent 844
   * line's differ only in that one is cut short (SD-21).
   */
  matchedOnTruncatedKey: z.string().optional(),
});
export type EDI849LineResult = z.infer<typeof EDI849LineResultSchema>;

/**
 * Submitted 844 line data (original claim data from rexel_e844sd)
 */
/**
 * One SPA record naming `N1*BY` codes ("DUNS") for a contract, with its full
 * start/end window — what a claim's DUNS is checked against (Schneider).
 * Shared by the 849 response detail and the 844 transaction detail.
 */
export const SpaBuyerCodesRecordSchema = z.object({
  spaId: UUIDSchema,
  /** `YYYY-MM-DD`, inclusive. */
  startDate: z.string(),
  endDate: z.string(),
  /** Distinct non-blank `BY` codes in document order, verbatim (leading zeros kept). */
  codes: z.array(z.string()),
});

/** Every SPA record on file for a contract that names a buyer, newest start first. */
export const SpaBuyerCodesSchema = z.object({
  contractNumber: z.string(),
  spas: z.array(SpaBuyerCodesRecordSchema),
});

export type SpaBuyerCodesRecord = z.infer<typeof SpaBuyerCodesRecordSchema>;
export type SpaBuyerCodes = z.infer<typeof SpaBuyerCodesSchema>;

const SubmittedLineDataSchema = z.object({
  sdE844dId: z.string().nullable().optional(),
  submissionId: z.string().nullable().optional(),
  invoiceNumber: z.string(),
  orderLineNumber: z.number(),
  batchControlNumber: z.string(),
  claimAmount: z.number(),
  buyerCost: z.number(),
  shippedQuantity: z.number(),
  customerName: z.string(),
  itemNumber: z.string(),
  supplierItemCode: z.string(),
  poNumber: z.string(),
  orderNumber: z.number(),
  branchName: z.string(),
  supplierContractNo: z.string(),
});

/**
 * Linked VDM summary (for 849 response cross-referencing via 844 submissions)
 */
const LinkedVDMSummarySchema = z.object({
  id: UUIDSchema,
  gentlyVdmNumber: z.number().int(),
  vdmNumber: z.number().int().nullable(),
  status: z.string(),
  grossAmount: z.string(),
});

/**
 * Linked 844 submission summary (for 849 response cross-referencing)
 */
const LinkedSubmissionSummarySchema = z.object({
  id: UUIDSchema,
  submissionNumber: z.number().int(),
  companyName: z.string().nullable(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  status: z.string(),
  deliveryMethod: z.string(),
  totalClaimedAmount: z.string(),
  sentAt: z.string().nullable(),
  vdm: LinkedVDMSummarySchema.nullable(),
  vdms: z.array(LinkedVDMSummarySchema).optional(),
});

/**
 * Non-decision context extracted from a supplier S&D email (e.g. a pre-denial
 * inquiry). Informational only — never affects line decisions or amounts.
 */
export const EDI849AdvisorySchema = z.object({
  summary: z.string(),
  requestedAction: z.string().nullable(),
  contact: z.string().nullable(),
  lines: z.array(
    z.object({
      supplierProductCode: z.string().nullable(),
      description: z.string().nullable(),
      issueCode: z.string().nullable(),
      issueText: z.string().nullable(),
      matchedSdE844dId: z.string().nullable(),
    }),
  ),
  source: z.literal("email"),
  extractedAt: z.string(),
});
export type EDI849Advisory = z.infer<typeof EDI849AdvisorySchema>;

/**
 * EDI849 Response schema
 */
export const EDI849ResponseSchema = z.object({
  id: UUIDSchema,
  /**
   * The response's source email: an email response's first decision email (it
   * has many — ADR 0005), an EDI response's carrier email. Null when none.
   */
  inboundEmailId: UUIDSchema.nullable(),
  supplierId: z.string(),
  supplierName: z.string(),
  /** Rollup of what the supplier said about the lines we submitted. */
  lineResultsStatus: z.string(),
  /** What we're doing about the claim. Null until someone triages it. */
  status: SD849StatusSchema.nullable(),
  acceptedCount: z.number().int().nonnegative(),
  rejectedCount: z.number().int().nonnegative(),
  partialCount: z.number().int().nonnegative(),
  lineResults: z.array(EDI849LineResultSchema),
  sourceType: z.string(),
  parentResponseId: UUIDSchema.nullable(),
  processedAt: z.string(),
  rawEdiContent: z.string().nullable().optional(),
  batchNumbers: z.array(z.string()).optional(),
  divisions: z.array(z.string()).optional(),
  /**
   * Persisted division — resolved from the EDI buyer segment for EDI responses,
   * and for email responses from the 844s their lines linked to, the division
   * name in the subject, or the line's selling branch. Null if unresolved.
   */
  divisionCode: z.string().nullable().optional(),
  /**
   * Whether {@link divisionCode} was derived or set by a user. A "manual"
   * division is never re-derived.
   */
  divisionSource: z.enum(["derived", "manual"]).nullable().optional(),
  /**
   * Human-readable names for `divisions`, in the same order. The UI shows these
   * — ops read "Rexel Atlantic", not "A".
   */
  divisionNames: z.array(z.string()).optional(),
  /** Per-status dollar amounts for this response (credited for accepted/partial, claimed for partial/rejected). */
  amounts: z
    .object({
      acceptedApproved: z.number(),
      partialApproved: z.number(),
      partialClaimed: z.number(),
      /**
       * Claimed $ on rejected lines. Suppliers usually echo $0 on rejected
       * lines in the 849, so when the 849 value is not meaningful this falls
       * back to the matched 844 submitted claim amount. {@link rejectedClaimedSource}
       * records where the figure came from.
       */
      rejectedClaimed: z.number(),
      /**
       * Where {@link rejectedClaimed} came from: `"response"` = the 849 echo,
       * `"submitted"` = the 844 we sent (849 echoed $0), `"mixed"` = both
       * across different rejected lines, `"none"` = no rejected amount.
       */
      rejectedClaimedSource: z.enum(["response", "submitted", "mixed", "none"]),
      /**
       * Total claimed $ across all lines (claimReceivedAmount, with the same
       * 844 submitted-claim fallback applied when the 849 echoed $0). This is
       * what the whole response is "worth".
       */
      totalClaimed: z.number(),
      /** Total credited $ across all lines (what the supplier actually approved: accepted + partial). */
      totalCredited: z.number(),
    })
    .optional(),
  email: ResponseEmailSchema.optional(),
  /**
   * EVERY email linked to this 849, oldest first — the claim's conversation.
   *
   * Beside `email` rather than replacing it: that field is the SOURCE email (an
   * email response's first decision email), and several callers read it as such.
   * This one always contains it too, flagged `isAnchor`, so a consumer rendering
   * the list needs no second source.
   */
  claimEmails: z.array(ClaimEmailSchema).default([]),
  linkedSubmissions: z.array(LinkedSubmissionSummarySchema).optional(),
  /**
   * VDMs **attached by hand** to this 849's transactions after the 844 was sent
   * (`sd_transaction_vdm_links`) — a debit memo cut once the supplier
   * responded, to carry the credit.
   *
   * Deliberately separate from `linkedSubmissions[].vdms`, which are the LIVE
   * VDMs (the number that went out in the 844D's RDDM# slot). Live vs attached
   * is a real financial distinction and the UI must not blur the two. Attached
   * memos hang off a TRANSACTION, not a submission, so they are response-scoped
   * rather than nested under a submission.
   *
   * The VDM# filter matches both, so the list has to be able to show both.
   */
  attachedVdms: z.array(LinkedVDMSummarySchema).optional(),
  /**
   * New 844 submissions that were created by resubmitting rejected/partial
   * lines of THIS 849 (clone model). Distinct from `linkedSubmissions`, which
   * are the original submissions this 849 responded to.
   */
  resubmittedSubmissions: z.array(LinkedSubmissionSummarySchema).optional(),
  submittedLines: z.array(SubmittedLineDataSchema).optional(),
  /**
   * For each contract on a rejection-code-1 line, the SPA records on file that
   * name `N1*BY` codes ("DUNS") and state a full start/end window — the line shows the ones
   * active on its invoice date beside the `buyerIdCode` we submitted. Empty
   * when no line is code 1 or no SPA names a buyer for the contract.
   */
  spaBuyerCodes: z.array(SpaBuyerCodesSchema).default([]),
  investigationStatus: z
    .enum(["pending", "running", "completed", "failed"])
    .nullable()
    .optional(),
  investigationResults: z
    .array(
      z.object({
        lineIndex: z.number().int().nonnegative(),
        rejectionCode: z.string().nullable(),
        gentlyRejectionCode: z.string().nullable(),
        strategySource: z.enum([
          "user_configured",
          "supplier_default",
          "generic_default",
          "none",
        ]),
        rootCause: z.string(),
        rootCauseCategory: z.enum([
          "missing_bpid",
          "wrong_bpid",
          "stale_bpid",
          "vendor_number_mismatch",
          "item_ineligible",
          "contract_ineligible",
          "unknown",
        ]),
        evidence: z.array(
          z.object({ source: z.string(), finding: z.string() }),
        ),
        suggestedFix: z.string().nullable(),
        suggestedFixType: z
          .enum(["add_bpid", "update_bpid", "resubmit", "escalate"])
          .nullable(),
        fixParameters: z.record(z.string(), z.unknown()).nullable(),
        confidence: z.number().min(0).max(1),
        isOurIssue: z.boolean(),
      }),
    )
    .nullable()
    .optional(),
  investigatedAt: z.string().nullable().optional(),
  investigationEnabled: z.boolean().optional(),
  /** Non-decision supplier context (detail view only — full payload). */
  advisories: z.array(EDI849AdvisorySchema).nullable().optional(),
  /** Lightweight flag for the list view (full advisories only on detail). */
  hasAdvisory: z.boolean().optional(),
  investigationFeedback: z
    .array(
      z.object({
        lineIndex: z.number(),
        decision: z.enum(["agree", "disagree"]),
        correctRootCause: z.string().nullable().optional(),
        correctFix: z.string().nullable().optional(),
        reason: z.string().nullable().optional(),
        feedbackAt: z.string(),
        feedbackByUserId: z.string(),
      }),
    )
    .nullable()
    .optional(),
});
export type EDI849Response = z.infer<typeof EDI849ResponseSchema>;

// ----------------------------------------
// List EDI849 Responses
// ----------------------------------------
/**
 * The filters that select 849 responses.
 *
 * Shared by the LIST and its Excel EXPORT — deliberately one definition, because
 * an export that doesn't match the screen it was launched from is worse than no
 * export, and two copies of eight filters is how a new one lands on only one of
 * them. The backend mirrors this with a single `buildResponseListWhere`.
 */
export const EDI849ResponseFilterSchema = z.object({
  supplierIds: z.array(z.string()).optional(),
  /** Line-results rollup filter — what the supplier said about the lines. */
  statuses: z.array(EDI849LineResultsStatusSchema).optional(),
  /**
   * Claim-status filter — what WE are doing about the claim.
   *
   * Deliberately NOT named `statuses` with the line-results filter renamed
   * around it, which is what the plan doc proposed: the two filters would then
   * swap meanings on one field name, and a browser holding the previous bundle
   * would send `statuses: ["accepted"]` into the claim-status enum and get its
   * whole list request rejected. Shared `?statuses=` URLs keep working too.
   */
  claimStatuses: z.array(SD849StatusFilterSchema).optional(),
  /** Source type filter — "email" (Excel attachment) or "edi" (X12 attachment). */
  sourceTypes: z.array(z.enum(["email", "edi"])).optional(),
  /** Processed date range — entries like "gte:YYYYMMDD" / "lte:YYYYMMDD" / "eq:YYYYMMDD". Matches the DateFilter component's output. */
  processedAt: z.array(z.string()).optional(),
  /** Browser timezone offset in minutes (from Date.getTimezoneOffset()). Positive = west of UTC. Used to anchor YYYYMMDD dates to the user's local day. */
  timezoneOffset: z.number().int().min(-840).max(720).optional(),
  /** Batch number filter — each entry is an exact batch ("12345") or an inclusive range ("12341-12349"). Entries are OR'd. Matches the BatchNumberFilter component's output. */
  batchNumber: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  /**
   * VDM number filter — exact ("45155") or an inclusive range ("45100-45199");
   * entries are OR'd. Matches BOTH ways a VDM reaches a 849: the LIVE memo
   * whose number went out in the 844D's RDDM# slot (`sd_vdm_transactions`) and
   * memos attached by hand after the response came back
   * (`sd_transaction_vdm_links`). Both are "the VDM on this 849" to the team.
   */
  vdmNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  /** Division codes to filter by (matches linked 844 submission division_code). */
  divisions: z.array(z.string()).optional(),
  /**
   * Last-updated date range — same `gte:`/`lte:`/`eq:` + `YYYYMMDD` entries as
   * {@link processedAt}, and anchored to the user's day by the same
   * `timezoneOffset`.
   *
   * Filters on the ROLLED-UP value, not on `created_at`: a claim created in May
   * and chased in June is inside a June range and outside a May one. Filtering
   * the created date instead would return the exact opposite set, which is the
   * whole reason this filter is separate from `processedAt` rather than a
   * relabelling of it.
   */
  lastUpdatedAt: z.array(z.string()).optional(),
});

/**
 * Columns the responses list can be ordered by.
 *
 * Deliberately an enum rather than a free-string column name: it is
 * interpolated into an ORDER BY, and the set of things worth sorting by is
 * small and known. `processedAt` names the existing default so a caller can
 * ask for it explicitly and get back to it.
 */
export const EDI849ResponseSortBySchema = z.enum([
  "processedAt",
  "lastUpdatedAt",
]);
export type EDI849ResponseSortBy = z.infer<typeof EDI849ResponseSortBySchema>;

export const ListEDI849ResponsesInputSchema = EDI849ResponseFilterSchema.extend(
  {
    limit: z.number().int().min(1).max(100).optional(),
    offset: z.number().int().min(0).optional(),
    /**
     * Sort column. Absent means `processedAt` DESC — the order this list has
     * always used. Left as the default on purpose: shared `?`-URLs and every
     * existing caller depend on it, and silently reordering the page under
     * people is worse than not offering the sort.
     */
    sortBy: EDI849ResponseSortBySchema.optional(),
    sortDir: z.enum(["asc", "desc"]).optional(),
  },
);
export type ListEDI849ResponsesInput = z.infer<
  typeof ListEDI849ResponsesInputSchema
>;

/**
 * A row in the 849 responses list — {@link EDI849ResponseSchema} minus the
 * heavy `lineResults` array, plus a `lineCount` total.
 *
 * `lineResults` averages ~73 kB per response (worst case seen: 5.3 MB), so a
 * 25-row page shipped ~1.7 MB of it — uncompressed, to users on ~0.25 Mbps —
 * and the list never rendered a single line. Everything the list needs is
 * already derived server-side from those lines: `batchNumbers`, `divisions`,
 * `amounts`, the per-status counts, and now `lineCount`. The full array is
 * still returned by the `get` (detail) endpoint.
 */
export const EDI849ResponseListItemSchema = EDI849ResponseSchema.omit({
  lineResults: true,
}).extend({
  /**
   * How many parsed entries are in this response's `lineResults`.
   *
   * NOT interchangeable with `acceptedCount + partialCount + rejectedCount`.
   * Those record the decision; this records how much line-level detail we
   * managed to parse, and the two legitimately diverge when a supplier sends a
   * header-level decision with no itemised lines — e.g. 5 Superior Essex email
   * responses on prod carry `rejectedCount: 1` with an empty `lineResults`. A
   * row showing 0 lines against a non-zero status count is that case, not a
   * bug: it means we recorded a decision we have no line breakdown for.
   */
  lineCount: z.number().int().nonnegative(),
  /**
   * When the supplier last said anything about this claim — the column the
   * list sorts and filters on.
   *
   * The NEWEST `received_at` across every email linked to the response, both
   * the `sd_849_response_emails` join rows and the legacy anchor FK, falling
   * back to the response's own `created_at` when no email exists at all.
   *
   * Non-nullable, unlike `email.receivedAt`. That field answers "when did the
   * supplier's email arrive", where a created date would invent an email that
   * never existed, so it stays null when unknown. This one answers "when did
   * we last hear about this claim", and for an EDI 849 the X12 transaction IS
   * that communication — there is no email to be missing.
   *
   * List-only: the detail page already renders the whole conversation through
   * `claimEmails`, so it has no use for the rolled-up maximum.
   */
  lastUpdatedAt: z.string(),
});
export type EDI849ResponseListItem = z.infer<
  typeof EDI849ResponseListItemSchema
>;

export const EDI849ResponseTotalsSchema = z.object({
  totalAcceptedLines: z.number().int().nonnegative(),
  totalRejectedLines: z.number().int().nonnegative(),
  totalPartialLines: z.number().int().nonnegative(),
  /**
   * Credited dollars across accepted + partial lines — **signed**, and NOT
   * `.nonnegative()`.
   *
   * A credited amount is signed at source: an EDI line's `AMT*1` carries a
   * `C`/`D` flag and a debit charge-back is genuinely negative, while an
   * email/Excel credit note can map a negative column. So a filter that
   * happens to select mostly reversals sums below zero, and constraining this
   * made the whole endpoint 500 — the page could not render AT ALL for such a
   * filter, rather than showing an unexpected number.
   *
   * The per-row {@link EDI849ResponseListItemSchema} `amounts` have always been
   * plain `z.number()`. This was the aggregate of those rows contradicting
   * them.
   */
  totalApprovedAmount: z.number(),
});

export const ListEDI849ResponsesOutputSchema = z.object({
  responses: z.array(EDI849ResponseListItemSchema),
  pagination: EDI849PaginationSchema,
});
export type ListEDI849ResponsesResponse = z.infer<
  typeof ListEDI849ResponsesOutputSchema
>;

export const listEDI849ResponsesContract = oc
  .input(ListEDI849ResponsesInputSchema)
  .output(ListEDI849ResponsesOutputSchema)
  .route({
    summary: "List supplier 849 claim responses",
    description:
      "Paged list of supplier ship-and-debit claim responses, filterable by supplier, line outcome, claim status, source, division, batch, VDM number and dates.",
  });

/**
 * The list's stat-card totals, on their own endpoint.
 *
 * Input is exactly the list's filters — no page, no sort — so the page can key
 * the query on filters alone: flipping a page or re-sorting reuses the cached
 * totals, and the table never waits on the aggregate.
 */
export const EDI849ResponseTotalsInputSchema = EDI849ResponseFilterSchema;
export type EDI849ResponseTotalsInput = z.infer<
  typeof EDI849ResponseTotalsInputSchema
>;
export type EDI849ResponseTotals = z.infer<typeof EDI849ResponseTotalsSchema>;

export const edi849ResponseTotalsContract = oc
  .input(EDI849ResponseTotalsInputSchema)
  .output(EDI849ResponseTotalsSchema)
  .route({
    summary: "Get 849 response list totals",
    description:
      "Aggregate stat-card totals for supplier claim responses matching the same filters as the response list, without paging or sorting.",
  });

// ----------------------------------------
// Get EDI849 Response
// ----------------------------------------
export const GetEDI849ResponseInputSchema = z.object({
  id: UUIDSchema,
});
export type GetEDI849ResponseInput = z.infer<
  typeof GetEDI849ResponseInputSchema
>;

export const GetEDI849ResponseOutputSchema = EDI849ResponseSchema.nullable();
export type GetEDI849ResponseResponse = z.infer<
  typeof GetEDI849ResponseOutputSchema
>;

export const getEDI849ResponseContract = oc
  .input(GetEDI849ResponseInputSchema)
  .output(GetEDI849ResponseOutputSchema)
  .route({
    summary: "Get an 849 claim response",
    description:
      "One supplier claim response by id with its full line results, linked 844 submissions and VDMs; returns null when not found.",
  });

// ----------------------------------------
// Delete EDI849 Response (Admin only)
// ----------------------------------------
export const DeleteEDI849ResponseInputSchema = z.object({
  id: UUIDSchema,
});
export type DeleteEDI849ResponseInput = z.infer<
  typeof DeleteEDI849ResponseInputSchema
>;

export const DeleteEDI849ResponseOutputSchema = z.object({
  success: z.boolean(),
});
export type DeleteEDI849ResponseResponse = z.infer<
  typeof DeleteEDI849ResponseOutputSchema
>;

export const deleteEDI849ResponseContract = oc
  .input(DeleteEDI849ResponseInputSchema)
  .output(DeleteEDI849ResponseOutputSchema)
  .route({
    summary: "Delete an 849 claim response",
    description:
      "Admin-only: permanently deletes one supplier claim response from the organization.",
  });

// ----------------------------------------
// Update Supplier on 849 Response (single)
// ----------------------------------------
export const UpdateEDI849ResponseSupplierInputSchema = z.object({
  id: UUIDSchema,
  supplierId: z.string().uuid(),
});
export type UpdateEDI849ResponseSupplierInput = z.infer<
  typeof UpdateEDI849ResponseSupplierInputSchema
>;

export const UpdateEDI849ResponseSupplierOutputSchema = z.object({
  success: z.boolean(),
  supplierId: z.string(),
  supplierName: z.string(),
});
export type UpdateEDI849ResponseSupplierOutput = z.infer<
  typeof UpdateEDI849ResponseSupplierOutputSchema
>;

export const updateEDI849ResponseSupplierContract = oc
  .input(UpdateEDI849ResponseSupplierInputSchema)
  .output(UpdateEDI849ResponseSupplierOutputSchema)
  .route({
    summary: "Assign supplier to an 849 response",
    description:
      "Sets the supplier on one claim response, logs it on the timeline, and saves the original name as an alias for future automatic matching.",
  });

// ----------------------------------------
// Set the Division on an email-sourced 849 Response
// ----------------------------------------
export const SetEDI849ResponseDivisionInputSchema = z.object({
  id: UUIDSchema,
  /** Rexel division code, e.g. "A". Null clears it back to underived. */
  divisionCode: z.string().min(1).max(8).nullable(),
});
export type SetEDI849ResponseDivisionInput = z.infer<
  typeof SetEDI849ResponseDivisionInputSchema
>;

export const SetEDI849ResponseDivisionOutputSchema = z.object({
  id: UUIDSchema,
  divisionCode: z.string().nullable(),
  divisionName: z.string().nullable(),
  /** False when the submitted value was already the stored one. */
  changed: z.boolean(),
});
export type SetEDI849ResponseDivisionOutput = z.infer<
  typeof SetEDI849ResponseDivisionOutputSchema
>;

export const setEDI849ResponseDivisionContract = oc
  .input(SetEDI849ResponseDivisionInputSchema)
  .output(SetEDI849ResponseDivisionOutputSchema)
  .route({
    method: "POST",
    summary: "Set division on an 849 response",
    description:
      "Sets or clears the division code on an email-sourced claim response, stopping automatic re-derivation and recording the change on its timeline.",
  });

// ----------------------------------------
// Set the claim Status on an 849 Response
// ----------------------------------------
export const SetEDI849ResponseStatusInputSchema = z.object({
  id: UUIDSchema,
  /** Null clears the status, putting the claim back to untriaged. */
  status: SD849StatusSchema.nullable(),
});
export type SetEDI849ResponseStatusInput = z.infer<
  typeof SetEDI849ResponseStatusInputSchema
>;

export const SetEDI849ResponseStatusOutputSchema = z.object({
  id: UUIDSchema,
  status: SD849StatusSchema.nullable(),
  /**
   * False when the submitted value was already the stored one. The write is
   * still a success — it just recorded nothing, so the caller knows no timeline
   * entry appeared.
   */
  changed: z.boolean(),
});
export type SetEDI849ResponseStatusOutput = z.infer<
  typeof SetEDI849ResponseStatusOutputSchema
>;

export const setEDI849ResponseStatusContract = oc
  .input(SetEDI849ResponseStatusInputSchema)
  .output(SetEDI849ResponseStatusOutputSchema)
  .route({
    method: "POST",
    summary: "Set claim status on an 849 response",
    description:
      "Sets or clears our internal claim status (what we are doing about it) on a claim response and records the transition on its timeline.",
  });

// ----------------------------------------
// Bulk Assign Supplier to unmatched 849 Responses
// ----------------------------------------
export const BulkAssignSupplierInputSchema = z.object({
  responseIds: z.array(UUIDSchema).min(1),
  supplierId: z.string().uuid(),
});
export type BulkAssignSupplierInput = z.infer<
  typeof BulkAssignSupplierInputSchema
>;

export const BulkAssignSupplierOutputSchema = z.object({
  success: z.boolean(),
  updatedCount: z.number().int().nonnegative(),
  supplierName: z.string(),
});
export type BulkAssignSupplierOutput = z.infer<
  typeof BulkAssignSupplierOutputSchema
>;

export const bulkAssignSupplierContract = oc
  .input(BulkAssignSupplierInputSchema)
  .output(BulkAssignSupplierOutputSchema)
  .route({
    summary: "Bulk assign supplier to 849 responses",
    description:
      "Assigns one supplier to many claim responses at once, logs each on its timeline, and saves original names as aliases for future matching.",
  });

// ----------------------------------------
// User Response Schemas
// ----------------------------------------

/**
 * User response status enum
 */
export const SD849UserResponseStatusSchema = z.enum([
  "in_progress",
  "ready_to_send",
  "sent",
]);
export type SD849UserResponseStatus = z.infer<
  typeof SD849UserResponseStatusSchema
>;

/**
 * Line decision by user
 */
export const LineDecisionSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  decision: z.enum(["agree", "disagree"]),
  reason: z.string().optional(),
  decidedAt: z.string(),
  decidedByUserId: z.string().uuid(),
});
export type LineDecision = z.infer<typeof LineDecisionSchema>;

/**
 * Proof attachment info
 */
export const ProofAttachmentSchema = z.object({
  id: UUIDSchema,
  fileName: z.string(),
  fileSize: z.number(),
  mimeType: z.string(),
  uploadedAt: z.string(),
});
export type ProofAttachment = z.infer<typeof ProofAttachmentSchema>;

/**
 * Full user response schema
 */
export const SD849UserResponseSchema = z.object({
  id: UUIDSchema,
  sd849ResponseId: UUIDSchema,
  status: SD849UserResponseStatusSchema,
  lineDecisions: z.array(LineDecisionSchema),
  emailBodyTemplate: z.string().nullable().optional(),
  responseDocumentId: UUIDSchema.nullable().optional(),
  proofAttachments: z.array(ProofAttachmentSchema),
  sentAt: z.string().nullable().optional(),
  sentByUserId: UUIDSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SD849UserResponse = z.infer<typeof SD849UserResponseSchema>;

// ----------------------------------------
// Get or Create User Response
// ----------------------------------------
export const GetOrCreateUserResponseInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});
export type GetOrCreateUserResponseInput = z.infer<
  typeof GetOrCreateUserResponseInputSchema
>;

export const GetOrCreateUserResponseOutputSchema = SD849UserResponseSchema;
export type GetOrCreateUserResponseOutput = z.infer<
  typeof GetOrCreateUserResponseOutputSchema
>;

export const getOrCreateUserResponseContract = oc
  .input(GetOrCreateUserResponseInputSchema)
  .output(GetOrCreateUserResponseOutputSchema)
  .route({
    summary: "Get or start a reply to an 849",
    description:
      "Returns the user's draft reply to a supplier claim response, with line decisions, email body and proof attachments, creating it if none exists.",
  });

// ----------------------------------------
// Save Line Decision
// ----------------------------------------
export const SaveLineDecisionInputSchema = z.object({
  userResponseId: UUIDSchema,
  lineIndex: z.number().int().nonnegative(),
  decision: z.enum(["agree", "disagree"]),
  reason: z.string().optional(),
});
export type SaveLineDecisionInput = z.infer<typeof SaveLineDecisionInputSchema>;

export const SaveLineDecisionOutputSchema = z.object({
  success: z.boolean(),
  userResponse: SD849UserResponseSchema,
});
export type SaveLineDecisionOutput = z.infer<
  typeof SaveLineDecisionOutputSchema
>;

export const saveLineDecisionContract = oc
  .input(SaveLineDecisionInputSchema)
  .output(SaveLineDecisionOutputSchema)
  .route({
    summary: "Save line decision on an 849 reply",
    description:
      "Records agree or disagree, with an optional reason, for one line of the draft reply and returns the updated reply.",
  });

// ----------------------------------------
// Update Email Body
// ----------------------------------------
export const UpdateEmailBodyInputSchema = z.object({
  userResponseId: UUIDSchema,
  emailBodyTemplate: z.string(),
});
export type UpdateEmailBodyInput = z.infer<typeof UpdateEmailBodyInputSchema>;

export const UpdateEmailBodyOutputSchema = z.object({
  success: z.boolean(),
});
export type UpdateEmailBodyOutput = z.infer<typeof UpdateEmailBodyOutputSchema>;

export const updateEmailBodyContract = oc
  .input(UpdateEmailBodyInputSchema)
  .output(UpdateEmailBodyOutputSchema)
  .route({
    summary: "Update 849 reply email body",
    description:
      "Saves the email body text on a draft reply to a supplier claim response.",
  });

// ----------------------------------------
// Get Proof Upload URL
// ----------------------------------------
export const GetProofUploadUrlInputSchema = z.object({
  userResponseId: UUIDSchema,
  fileName: z.string(),
  mimeType: z.string(),
  fileSize: z.number().int().positive(),
});
export type GetProofUploadUrlInput = z.infer<
  typeof GetProofUploadUrlInputSchema
>;

export const GetProofUploadUrlOutputSchema = z.object({
  uploadUrl: z.string(),
  documentId: UUIDSchema,
});
export type GetProofUploadUrlOutput = z.infer<
  typeof GetProofUploadUrlOutputSchema
>;

export const getProofUploadUrlContract = oc
  .input(GetProofUploadUrlInputSchema)
  .output(GetProofUploadUrlOutputSchema)
  .route({
    summary: "Get upload URL for 849 reply proof",
    description:
      "Returns a presigned upload URL and document id for a proof file attached to a draft reply; confirm the upload afterwards.",
  });

// ----------------------------------------
// Confirm Proof Upload
// ----------------------------------------
export const ConfirmProofUploadInputSchema = z.object({
  userResponseId: UUIDSchema,
  documentId: UUIDSchema,
});
export type ConfirmProofUploadInput = z.infer<
  typeof ConfirmProofUploadInputSchema
>;

export const ConfirmProofUploadOutputSchema = z.object({
  success: z.boolean(),
  proofAttachments: z.array(ProofAttachmentSchema),
});
export type ConfirmProofUploadOutput = z.infer<
  typeof ConfirmProofUploadOutputSchema
>;

export const confirmProofUploadContract = oc
  .input(ConfirmProofUploadInputSchema)
  .output(ConfirmProofUploadOutputSchema)
  .route({
    summary: "Confirm 849 reply proof upload",
    description:
      "Attaches an uploaded proof document to the draft reply and returns its current proof attachments.",
  });

// ----------------------------------------
// Remove Proof Attachment
// ----------------------------------------
export const RemoveProofAttachmentInputSchema = z.object({
  userResponseId: UUIDSchema,
  documentId: UUIDSchema,
});
export type RemoveProofAttachmentInput = z.infer<
  typeof RemoveProofAttachmentInputSchema
>;

export const RemoveProofAttachmentOutputSchema = z.object({
  success: z.boolean(),
  proofAttachments: z.array(ProofAttachmentSchema),
});
export type RemoveProofAttachmentOutput = z.infer<
  typeof RemoveProofAttachmentOutputSchema
>;

export const removeProofAttachmentContract = oc
  .input(RemoveProofAttachmentInputSchema)
  .output(RemoveProofAttachmentOutputSchema)
  .route({
    summary: "Remove proof from an 849 reply",
    description:
      "Detaches a proof document from the draft reply and returns the remaining proof attachments.",
  });

// ----------------------------------------
// Generate Response Excel
// ----------------------------------------
export const GenerateResponseExcelInputSchema = z.object({
  userResponseId: UUIDSchema,
});
export type GenerateResponseExcelInput = z.infer<
  typeof GenerateResponseExcelInputSchema
>;

export const GenerateResponseExcelOutputSchema = z.object({
  downloadUrl: z.string(),
  documentId: UUIDSchema,
});
export type GenerateResponseExcelOutput = z.infer<
  typeof GenerateResponseExcelOutputSchema
>;

export const generateResponseExcelContract = oc
  .input(GenerateResponseExcelInputSchema)
  .output(GenerateResponseExcelOutputSchema)
  .route({
    summary: "Generate 849 reply Excel",
    description:
      "Builds the reply spreadsheet from the user's line decisions, stores it as a document, and returns a download link.",
  });

// ----------------------------------------
// Send Response to Supplier
// ----------------------------------------
export const SendResponseToSupplierInputSchema = z.object({
  userResponseId: UUIDSchema,
  emailBody: z.string(),
});
export type SendResponseToSupplierInput = z.infer<
  typeof SendResponseToSupplierInputSchema
>;

export const SendResponseToSupplierOutputSchema = z.object({
  success: z.boolean(),
  sentAt: z.string(),
});
export type SendResponseToSupplierOutput = z.infer<
  typeof SendResponseToSupplierOutputSchema
>;

export const sendResponseToSupplierContract = oc
  .input(SendResponseToSupplierInputSchema)
  .output(SendResponseToSupplierOutputSchema)
  .route({
    summary: "Mark 849 reply as sent to supplier",
    description:
      "Saves the email body and marks a fully decided reply as sent; no email is actually delivered. Fails if line decisions are incomplete.",
  });

// ----------------------------------------
// Download 849 Responses Excel
// ----------------------------------------
/**
 * Which workbook to build.
 *
 * - `standard` — the layout the team sends to AP for Schneider and Rockwell.
 *   Left exactly as it was.
 * - `expanded` — the same sheets plus, per response and per line, what we
 *   claimed, what accepted / partial lines credited, what rejected lines
 *   claimed, and the open amount — for suppliers whose 849s are not mirrored
 *   by a statement.
 */
export const Download849ResponsesExcelLayoutSchema = z.enum([
  "standard",
  "expanded",
]);
export type Download849ResponsesExcelLayout = z.infer<
  typeof Download849ResponsesExcelLayoutSchema
>;

/** The same filters as the list — see {@link EDI849ResponseFilterSchema}. */
export const Download849ResponsesExcelInputSchema =
  EDI849ResponseFilterSchema.extend({
    /** Absent means `standard`, so existing callers get the same file. */
    layout: Download849ResponsesExcelLayoutSchema.optional(),
  });
export type Download849ResponsesExcelInput = z.infer<
  typeof Download849ResponsesExcelInputSchema
>;

export const Download849ResponsesExcelOutputSchema = z.object({
  /** base64-encoded .xlsx bytes */
  excel: z.string(),
  filename: z.string(),
});
export type Download849ResponsesExcelOutput = z.infer<
  typeof Download849ResponsesExcelOutputSchema
>;

export const download849ResponsesExcelContract = oc
  .input(Download849ResponsesExcelInputSchema)
  .output(Download849ResponsesExcelOutputSchema)
  .route({
    method: "POST",
    summary: "Export 849 responses to Excel",
    description:
      "Exports claim responses matching the list filters as an Excel workbook with summary and per-line detail sheets, returned as base64.",
  });

// ----------------------------------------
// Trigger Investigation
// ----------------------------------------
const TriggerInvestigationInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});

const TriggerInvestigationOutputSchema = z.object({
  success: z.boolean(),
  agentRunId: z.string().uuid().optional(),
  message: z.string(),
});

export const triggerInvestigationContract = oc
  .input(TriggerInvestigationInputSchema)
  .output(TriggerInvestigationOutputSchema)
  .route({
    method: "POST",
    summary: "Investigate rejected 849 claim lines",
    description:
      "Runs the AI investigation agent over a claim response's rejected or partial lines and saves its findings; requires investigation enabled for the supplier.",
  });

// ----------------------------------------
// Save Investigation Feedback
// ----------------------------------------
const SaveInvestigationFeedbackInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndex: z.number().int().min(0),
  decision: z.enum(["agree", "disagree"]),
  correctRootCause: z.string().optional(),
  correctFix: z.string().optional(),
  reason: z.string().optional(),
});

const SaveInvestigationFeedbackOutputSchema = z.object({
  success: z.boolean(),
});

export const saveInvestigationFeedbackContract = oc
  .input(SaveInvestigationFeedbackInputSchema)
  .output(SaveInvestigationFeedbackOutputSchema)
  .route({
    method: "POST",
    summary: "Save feedback on 849 investigation",
    description:
      "Records agree or disagree on the investigation agent's finding for one line, with optional correct root cause, fix and reason.",
  });

// ----------------------------------------
// 849 Rejection Resubmission
// ----------------------------------------

/** A transaction parked for resubmission (rows of the "To Resubmit" table). */
export const ResubmissionCandidateSchema = z.object({
  sdE844dId: UUIDSchema,
  rexelE844sdId: UUIDSchema,
  originalSubmissionId: UUIDSchema.nullable(),
  invoiceNo: z.string(),
  orderLineNo: z.number(),
  tradingPartner: z.string().nullable(),
  division: z.string().nullable(),
  sellingLocation: z.string().nullable(),
  claimAmount: z.number(),
  unitPriceWholesale: z.number().nullable(),
  unitPriceQuoted: z.number().nullable(),
  quantity: z.number().nullable(),
  supplierContractNo: z.string().nullable(),
  rejectionCode: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  dataChanged: z.boolean(),
  /** Full sd_e844d row (snake_case, same shape as the dashboard list) — fed to
   *  TransactionEditModal so 844D fields are edited in the standard modal. */
  transaction: z.object({ id: UUIDSchema }).passthrough(),
});
export type ResubmissionCandidate = z.infer<typeof ResubmissionCandidateSchema>;

export const GetResubmissionCandidatesInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  /** The transactions the user has disputed (frontend state) — read-only.
   *  Optional with an empty-array default: this field was added later, so a
   *  stale frontend bundle (or any caller) that omits it must not hard-fail
   *  input validation with a 422. The handler already treats `[]` as "no
   *  disputed transactions yet" (returns enablement + resubmitted locks only). */
  transactionIds: z.array(UUIDSchema).optional().default([]),
});
export const GetResubmissionCandidatesOutputSchema = z.object({
  resubmissionEnabled: z.boolean(),
  candidates: z.array(ResubmissionCandidateSchema),
  /** `invoice|orderLine` keys of lines already resubmitted (locked) for this 849. */
  resubmittedKeys: z.array(z.string()),
  /** SPAs on file for the candidates' contracts that name `N1*BY` codes — the
   *  edit modal's DUNS check. Empty for a supplier without the check. */
  spaBuyerCodes: z.array(SpaBuyerCodesSchema).default([]),
});
export const getResubmissionCandidatesContract = oc
  .input(GetResubmissionCandidatesInputSchema)
  .output(GetResubmissionCandidatesOutputSchema)
  .route({
    summary: "List 849 lines to resubmit",
    description:
      "Rejected transactions parked for resubmission on a claim response, whether resubmission is enabled for the supplier, and which lines were already resubmitted.",
  });

export const CheckResubmissionDataInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
});
export const ResubmissionDataCheckSchema = z.object({
  id: UUIDSchema,
  unchanged: z.boolean(),
  changedFields: z.array(z.string()),
});
export const CheckResubmissionDataOutputSchema = z.object({
  checks: z.array(ResubmissionDataCheckSchema),
});
export const checkResubmissionDataContract = oc
  .input(CheckResubmissionDataInputSchema)
  .output(CheckResubmissionDataOutputSchema)
  .route({
    method: "POST",
    summary: "Check resubmission data for changes",
    description:
      "For each transaction, reports whether its claim data changed since the original send, listing changed fields; read-only.",
  });

const ResubmissionSubmissionSummarySchema = z.object({
  id: UUIDSchema,
  division: z.string(),
  batchNumber: z.string().nullable(),
  status: z.string(),
  transactionCount: z.number(),
  totalClaimedAmount: z.string(),
});

/**
 * Which VDM the resubmitted 844 is billed under.
 *
 * - `reuse` (default) — carry the ORIGINAL 844's AS400 VDM number (RDDM#) onto
 *   the resubmitted 844D. Historic behaviour, and the faster path: the VDM is
 *   already confirmed, so the clone ships on the next poll.
 * - `new` — mint a fresh VDM for the resubmission (one per division + tax
 *   province, like a normal send). The new VDM starts unconfirmed, so the 844D
 *   waits for AS400's O1 confirmation before it uploads.
 */
export const ResubmitVdmModeSchema = z.enum(["reuse", "new"]);
export type ResubmitVdmMode = z.infer<typeof ResubmitVdmModeSchema>;

export const ResubmitRejectedInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  transactionIds: z.array(UUIDSchema).min(1),
  /** Per-transaction field overrides (snake_case → value), applied to the clone
   *  server-side. Frontend-staged edits — never written to the original. */
  edits: z.record(UUIDSchema, z.record(z.string(), z.unknown())).optional(),
  /** Required to proceed when any selected transaction has unchanged data. */
  confirmUnchanged: z.boolean().optional(),
  /**
   * Omitted means `reuse`, so existing callers keep today's behaviour exactly.
   * Deliberately `.optional()` rather than `.default()`: `ResubmitRejectedInput`
   * is a `z.infer` (output) type, so a default would make the field REQUIRED for
   * every TypeScript caller. The service applies the `reuse` default.
   */
  vdmMode: ResubmitVdmModeSchema.optional(),
});
export type ResubmitRejectedInput = z.infer<typeof ResubmitRejectedInputSchema>;
/**
 * The outcome of a resubmission, stored on the background job's payload under
 * `result` once the Temporal run succeeds. The frontend parses it back out of
 * the polled job with this schema — it is no longer the RPC response itself.
 *
 * Same reasoning as `Send844BatchResultSchema`: a resubmission ends in the same
 * `submitTransactions` call, so one spanning many transactions and DUNS
 * locations outlives the browser's ~300s fetch limit. A request that times out
 * mid-run leaves the user clicking Resubmit on a claim that already went out —
 * which the double-send guard then refuses, reporting a "failure" for work that
 * actually succeeded.
 */
export const Sd849ResubmitResultSchema = z.object({
  successful: z.array(UUIDSchema),
  failed: z.array(z.object({ id: UUIDSchema, reason: z.string() })),
  submissions: z.array(ResubmissionSubmissionSummarySchema),
  /** The original 844 submissions the resubmitted transactions came from. */
  resubmittedFromSubmissionIds: z.array(UUIDSchema),
  summary: z.object({
    totalRequested: z.int(),
    successCount: z.int(),
    failedCount: z.int(),
    submissionCount: z.int(),
  }),
});
export type Sd849ResubmitResult = z.infer<typeof Sd849ResubmitResultSchema>;

export const ResubmitRejectedOutputSchema = z.object({
  /** True when the call was blocked by the unchanged-data guard. */
  blockedByUnchangedData: z.boolean(),
  unchangedIds: z.array(UUIDSchema),
  /**
   * The job to poll for the result — null only when `blockedByUnchangedData`
   * is true, i.e. nothing was started. Validation (supplier gate, and the
   * "already resubmitted" check that makes a double click safe) still runs
   * synchronously, so an ineligible resubmission still fails fast rather than
   * through a job the user has to wait on.
   */
  backgroundJobId: UUIDSchema.nullable(),
});
export const resubmitRejectedContract = oc
  .input(ResubmitRejectedInputSchema)
  .output(ResubmitRejectedOutputSchema)
  .route({
    method: "POST",
    summary: "Resubmit rejected claim lines",
    description:
      "Starts a background job that sends selected rejected transactions as a new 844 claim, optionally with edits and a new VDM; blocks unchanged data unless confirmed.",
  });

// ----------------------------------------
// 849 Line Decisions (agree = not disputing / disagree = disputing)
// ----------------------------------------

/** Our stance on a line the supplier didn't fully credit. */
export const SD849LineDecisionValueSchema = z.enum(["agree", "disagree"]);
export type SD849LineDecisionValue = z.infer<
  typeof SD849LineDecisionValueSchema
>;

/**
 * One recorded decision about a line the supplier rejected. The identity and
 * claim amount are SNAPSHOTTED when the user decides, so the downstream report
 * shows what was decided rather than what the row looks like later.
 */
export const SD849LineDecisionSchema = z.object({
  id: UUIDSchema,
  /** Index into the response's `lineResults`. */
  lineIndex: z.number().int().nonnegative(),
  decision: SD849LineDecisionValueSchema,
  /** The supplier decision responded to: "rejected" or "partial". */
  lineStatus: z.string(),
  sdE844dId: UUIDSchema.nullable(),
  invoiceNo: z.string().nullable(),
  orderLineNo: z.number().int().nullable(),
  batchControlNo: z.string().nullable(),
  /** What we claimed on the 844 — the dollars at stake on this line. */
  claimAmount: z.number().nullable(),
  note: z.string().nullable(),
  decidedAt: z.coerce.date(),
  decidedByUserId: UUIDSchema.nullable(),
  decidedByUserName: z.string().nullable(),
});
export type SD849LineDecision = z.infer<typeof SD849LineDecisionSchema>;

/**
 * The decision state for a response: whether decisions are recorded at all
 * (`decisionsEnabled` — EDI + a decision-enabled supplier, resolved server-side
 * so the frontend never hardcodes a supplier list) plus what has been decided.
 *
 * Deliberately its own small query rather than fields on `get`: deciding must
 * refresh this, and refetching `get` would re-ship the whole `lineResults`
 * payload (~73 kB typical, 5 MB worst case) to users on ~0.25 Mbps links.
 */
const LineDecisionStateSchema = z.object({
  decisionsEnabled: z.boolean(),
  decisions: z.array(SD849LineDecisionSchema),
});

export const ListLineDecisionsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});
export const listLineDecisionsContract = oc
  .input(ListLineDecisionsInputSchema)
  .output(LineDecisionStateSchema)
  .route({
    method: "GET",
    summary: "List dispute decisions on an 849",
    description:
      "Whether line decisions are enabled for the claim response and the agree or dispute decisions recorded on its rejected lines.",
  });

export const RecordLineDecisionInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndexes: z.array(z.number().int().nonnegative()).min(1),
  decision: SD849LineDecisionValueSchema,
  note: z.string().trim().max(2000).optional(),
});
export const RecordLineDecisionOutputSchema = LineDecisionStateSchema.extend({
  /** How many rows this call wrote (re-recording the same decision is a no-op). */
  recorded: z.number().int().nonnegative(),
  /** Requested lines with nothing to decide (e.g. the supplier accepted them). */
  skipped: z.array(
    z.object({
      lineIndex: z.number().int().nonnegative(),
      /**
       * Stable code for the UI to translate. `reason` is English prose for logs
       * and the audit trail — never render it to a user (see the frontend's
       * gt-react rule). Optional so a client on an older bundle still parses a
       * response from a newer server.
       */
      reasonCode: z.enum(["line-not-found", "not-decidable"]).optional(),
      reason: z.string(),
      /** The supplier's verdict, when that is why the line can't be decided. */
      lineStatus: z.string().optional(),
    }),
  ),
});
export const recordLineDecisionContract = oc
  .input(RecordLineDecisionInputSchema)
  .output(RecordLineDecisionOutputSchema)
  .route({
    method: "POST",
    summary: "Record dispute decision on 849 lines",
    description:
      "Marks rejected or partial lines as agreed (accept the rejection) or disagreed (dispute it), with an optional note; skips lines with nothing to decide.",
  });

export const ClearLineDecisionsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndexes: z.array(z.number().int().nonnegative()).min(1),
});
export const ClearLineDecisionsOutputSchema = LineDecisionStateSchema.extend({
  cleared: z.number().int().nonnegative(),
});
export const clearLineDecisionsContract = oc
  .input(ClearLineDecisionsInputSchema)
  .output(ClearLineDecisionsOutputSchema)
  .route({
    method: "POST",
    summary: "Clear dispute decisions on 849 lines",
    description:
      "Removes recorded agree or dispute decisions from the given lines of a claim response, returning them to undecided.",
  });

// ----------------------------------------
// Post-send VDM links (attach an additional VDM to this 849's transactions)
// ----------------------------------------

/**
 * One VDM attached to an 844 transaction AFTER it was sent — a debit memo
 * created once the supplier responded, to carry the credit.
 *
 * Distinct from the transaction's LIVE VDM (`sd_vdm_transactions`), which is the
 * memo whose number went out in the 844D's RDDM# slot and is unique per
 * transaction. A transaction may accumulate any number of these.
 */
export const TransactionVdmLinkSchema = z.object({
  id: UUIDSchema,
  transactionId: UUIDSchema,
  vdmId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  /** AS400-assigned number; null while the VDM is still unconfirmed. */
  vdmNumber: z.int().nullable(),
  status: z.string(),
  note: z.string().nullable(),
  createdByUserName: z.string().nullable(),
});

/** An 844 transaction this 849 decided, offered as a link target. */
export const LinkableTransactionSchema = z.object({
  id: UUIDSchema,
  status: z.string(),
  invoiceNo: z.string().nullable(),
  orderLineNo: z.int().nullable(),
  claimAmount: z.number().nullable(),
  /** The VDM the 844 actually shipped under, for context ("already billed"). */
  liveVdmNumber: z.int().nullable(),
  /** Post-send attachments on this transaction, oldest first. */
  links: z.array(TransactionVdmLinkSchema),
});

export type TransactionVdmLink = z.infer<typeof TransactionVdmLinkSchema>;
export type VdmLinkTarget = z.infer<typeof LinkableTransactionSchema>;

export const ListVdmLinkTargetsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});
export const ListVdmLinkTargetsResponseSchema = z.object({
  transactions: z.array(LinkableTransactionSchema),
});

export const listVdmLinkTargetsContract = oc
  .input(ListVdmLinkTargetsInputSchema)
  .output(ListVdmLinkTargetsResponseSchema)
  .route({
    method: "GET",
    summary: "List transactions for VDM linking",
    description:
      "A claim response's 844 transactions with the VDM each shipped under and any debit memos attached after the supplier responded.",
  });

export const LinkVdmToTransactionsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  vdmId: UUIDSchema,
  transactionIds: z.array(UUIDSchema).min(1),
  note: z.string().trim().max(2000).optional(),
  /**
   * The user confirmed the Province override warning: link AB transactions to
   * a BC VDM (or the reverse). Honoured only for holders of SDAdmin Update.
   */
  provinceOverride: z.boolean().optional(),
});
export const LinkVdmToTransactionsResponseSchema = z.object({
  linkedCount: z.int(),
  /** Full post-link state, so the client can update its cache without a refetch. */
  transactions: z.array(LinkableTransactionSchema),
});

export const linkVdmToTransactionsContract = oc
  .input(LinkVdmToTransactionsInputSchema)
  .output(LinkVdmToTransactionsResponseSchema)
  .route({
    method: "POST",
    summary: "Attach VDM to 849 transactions",
    description:
      "Links an additional debit memo (VDM) to selected transactions of a claim response after sending, with an optional note.",
  });

export const UnlinkVdmFromTransactionsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  vdmId: UUIDSchema,
  transactionIds: z.array(UUIDSchema).min(1),
});
export const UnlinkVdmFromTransactionsResponseSchema = z.object({
  unlinkedCount: z.int(),
  transactions: z.array(LinkableTransactionSchema),
});

export const unlinkVdmFromTransactionsContract = oc
  .input(UnlinkVdmFromTransactionsInputSchema)
  .output(UnlinkVdmFromTransactionsResponseSchema)
  .route({
    method: "POST",
    summary: "Detach VDM from 849 transactions",
    description:
      "Removes a post-send debit memo (VDM) link from selected transactions of a claim response.",
  });

// ----------------------------------------
// List Ship & Debit-enabled companies (for workflow config dropdowns)
// ----------------------------------------
export const SDCompanySchema = z.object({
  id: UUIDSchema,
  /** co_companies.name */
  name: z.string(),
  /** Preferred display name; falls back to name when null */
  shortName: z.string().nullable(),
  /**
   * The `supplierName` of this company's active 849 config — the value a customer
   * mapping must be KEYED by, because that key is what the classifier hands to
   * `supplierConfigs.resolveByName`.
   *
   * It is usually NOT the company name: the config is `ABB` where the company is
   * `ABB ELECTRIFICATION CANADA ULC Commercial`. Keying on the company name builds
   * a mapping that routes the email and then finds no config for it.
   *
   * Null when the company has no active config, or when its configs are ambiguous.
   * The caller then falls back to the company name.
   */
  configName: z.string().nullable(),
  /**
   * The active config has the rejection-detail option (SD-6, ADR 0012): this
   * supplier's emails only add rejection detail to its EDI 849s, so the review
   * form does not offer "is an 849" for them (SD-9). The server refuses it too.
   */
  rejectionDetailOnly: z.boolean(),
  /**
   * The company's `ap_suppliers.id`s whose OWN configuration has the
   * rejection-detail option. A saved label names its supplier by such an id,
   * not by the company id, so the review form matches either.
   */
  rejectionDetailSupplierIds: z.array(UUIDSchema),
});
export type SDCompany = z.infer<typeof SDCompanySchema>;

export const ListSDCompaniesOutputSchema = z.object({
  companies: z.array(SDCompanySchema),
});
export type ListSDCompaniesResponse = z.infer<
  typeof ListSDCompaniesOutputSchema
>;

export const listSDCompaniesContract = oc
  .input(z.object({}))
  .output(ListSDCompaniesOutputSchema)
  .route({
    summary: "List ship-and-debit enabled companies",
    description:
      "Companies with the ship-and-debit module enabled, with display name and the active 849 config name used to key customer mappings.",
  });

// ----------------------------------------
// Activity timeline
// ----------------------------------------

/**
 * SD 849 Response Activity Type enum (matches the backend model
 * SD849ResponseActivityType).
 */
export const SD849ResponseActivityTypeSchema = z.enum([
  "created",
  "supplier_assigned",
  "lines_matched",
  "lines_rematched",
  "match_rerun",
  "resubmitted",
  "status_changed",
  "division_assigned",
  "email_attached",
  "evolved",
  "expert_feedback",
  "line_agreed",
  "line_disagreed",
  "line_decision_cleared",
  "vdm_linked",
  "vdm_unlinked",
  "comment_added",
  "attachment_refused",
  "correction_undone",
  "email_unlinked",
  "email_duplicate_copy",
]);
export type SD849ResponseActivityTypeContract = z.infer<
  typeof SD849ResponseActivityTypeSchema
>;

const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

const ActivityInboundEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string().nullable().optional(),
  from: z.string().nullable().optional(),
});

export const SD849ResponseActivitySchema = z.object({
  id: UUIDSchema,
  sd849ResponseId: UUIDSchema,
  activityType: SD849ResponseActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  inboundEmail: ActivityInboundEmailSchema.nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});
export type SD849ResponseActivity = z.infer<typeof SD849ResponseActivitySchema>;

export const ListSD849ResponseActivitiesInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  activityType: z.array(SD849ResponseActivityTypeSchema).optional(),
  limit: z.number().int().positive().max(200).optional(),
  offset: z.number().int().nonnegative().optional(),
  /** eventAt sort order (default "desc"). "asc" keeps the EARLIEST events when a page
   *  is capped — so the History timeline never loses its original `created` anchor. */
  order: z.enum(["asc", "desc"]).optional(),
});

export const ListSD849ResponseActivitiesResponseSchema = z.object({
  activities: z.array(SD849ResponseActivitySchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  }),
});
export type ListSD849ResponseActivitiesInput = z.infer<
  typeof ListSD849ResponseActivitiesInputSchema
>;
export type ListSD849ResponseActivitiesResponse = z.infer<
  typeof ListSD849ResponseActivitiesResponseSchema
>;

export const listSD849ResponseActivitiesContract = oc
  .input(ListSD849ResponseActivitiesInputSchema)
  .output(ListSD849ResponseActivitiesResponseSchema)
  .route({
    method: "GET",
    summary: "List 849 response activity timeline",
    description:
      "Paged timeline of events and comments on a claim response, filterable by activity type, ordered by event time.",
  });

// ----------------------------------------
// Comments (stored as `comment_added` activities on the timeline)
// ----------------------------------------

/**
 * Comments are activities, not their own entity — same shape as the 844
 * transaction's comments (`sd_e844d_activities`). The write endpoints therefore
 * return only `success`; the comment itself comes back from `listActivities`
 * with everything else on the timeline.
 */
const AddSD849ResponseCommentInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  text: z.string().trim().min(1).max(4000),
  /**
   * Scope this comment to one line of the response (index into `lineResults`).
   * Omitted = a comment on the claim as a whole, which is how every comment
   * behaved before line scoping existed.
   *
   * These are INTERNAL notes: the S&D team's own record of why a line was
   * handled the way it was. Nothing reads an activity into an outbound email or
   * file, so internal-only is a property of the storage, not a flag. The text
   * that MIGHT one day go to a supplier is the reason on a line decision
   * (`recordLineDecision.note`) — deliberately a different field.
   */
  lineIndex: z.number().int().nonnegative().optional(),
});

const SD849ResponseCommentResponseSchema = z.object({
  success: z.boolean(),
});

/** `activityId` is the comment's own activity row — author-only, within an hour. */
const EditSD849ResponseCommentInputSchema = z.object({
  activityId: UUIDSchema,
  text: z.string().trim().min(1).max(4000),
});

const DeleteSD849ResponseCommentInputSchema = z.object({
  activityId: UUIDSchema,
});

export type AddSD849ResponseCommentInput = z.infer<
  typeof AddSD849ResponseCommentInputSchema
>;

export const addSD849ResponseCommentContract = oc
  .input(AddSD849ResponseCommentInputSchema)
  .output(SD849ResponseCommentResponseSchema)
  .route({
    method: "POST",
    summary: "Add comment to an 849 response",
    description:
      "Adds an internal comment to a claim response's timeline, optionally scoped to one line; never sent to the supplier.",
  });

export const editSD849ResponseCommentContract = oc
  .input(EditSD849ResponseCommentInputSchema)
  .output(SD849ResponseCommentResponseSchema)
  .route({
    method: "POST",
    summary: "Edit comment on an 849 response",
    description:
      "Edits the text of your own comment on a claim response timeline, within an hour of posting.",
  });

export const deleteSD849ResponseCommentContract = oc
  .input(DeleteSD849ResponseCommentInputSchema)
  .output(SD849ResponseCommentResponseSchema)
  .route({
    method: "POST",
    summary: "Delete comment on an 849 response",
    description:
      "Deletes your own comment from a claim response timeline, within an hour of posting.",
  });

// ----------------------------------------
// Re-run 849 → 844 line matching
// ----------------------------------------
const RerunMatchingInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});

const RerunMatchingOutputSchema = z.object({
  /** False when the re-run couldn't be attempted (see `message`), not an error. */
  ran: z.boolean(),
  message: z.string(),
  newlyLinked: z.number().int().optional(),
  linkedBefore: z.number().int().optional(),
  linkedAfter: z.number().int().optional(),
  totalResponseLines: z.number().int().optional(),
});

export const rerunMatchingContract = oc
  .input(RerunMatchingInputSchema)
  .output(RerunMatchingOutputSchema)
  .route({
    method: "POST",
    summary: "Re-run 849 to 844 line matching",
    description:
      "Re-matches a claim response's still-unlinked lines to sent 844 transactions and logs the run; returns counts linked before and after.",
  });

// ----------------------------------------
// Undo a review correction / unlink an email from the claim (#7652)
// ----------------------------------------
const ReversedLineStateSchema = z.object({
  status: z.string(),
  acceptedAmount: z.string().nullable(),
});

/**
 * What an undo or unlink put back: the corrections taken back, each line's
 * from → to, what happened to the email on the claim, and whether the claim's
 * response closed because nothing remained (the claim reads unanswered).
 */
export const Sd849ReversalResultSchema = z.object({
  responseId: UUIDSchema,
  action: z.enum(["undo", "unlink"]),
  reversedActivityIds: z.array(UUIDSchema),
  lines: z.array(
    z.object({
      sdE844dId: UUIDSchema,
      from: ReversedLineStateSchema,
      to: ReversedLineStateSchema,
    }),
  ),
  email: z.enum(["detached", "demoted", "unchanged"]),
  responseClosed: z.boolean(),
});
export type Sd849ReversalResult = z.infer<typeof Sd849ReversalResultSchema>;

export const UndoSd849CorrectionInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  /** The correction's activity (a claim link or a stated decision). */
  activityId: UUIDSchema,
  reason: z.string().trim().max(2000).optional(),
});

export const undoSd849CorrectionContract = oc
  .input(UndoSd849CorrectionInputSchema)
  .output(Sd849ReversalResultSchema)
  .route({
    method: "POST",
    summary: "Undo a correction on an 849 claim",
    description:
      "Reverses one review correction (claim link or stated decision), restoring line decisions and possibly detaching the email or closing the response.",
  });

/**
 * Undo a multi-claim answer in one action: the claim link the correction is
 * part of, on every claim it decided — one reversal per claim, all or nothing.
 */
export const undoSd849ClaimLinkContract = oc
  .input(UndoSd849CorrectionInputSchema)
  .output(z.object({ reversals: z.array(Sd849ReversalResultSchema) }))
  .route({
    method: "POST",
    summary: "Undo a multi-claim email link",
    description:
      "Reverses a claim link across every claim it decided, all or nothing, restoring each claim's line decisions.",
  });

export const UnlinkSd849EmailInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  inboundEmailId: UUIDSchema,
  /** Why the email does not belong on this claim. Required. */
  reason: z.string().trim().min(1).max(2000),
});

export const unlinkSd849EmailContract = oc
  .input(UnlinkSd849EmailInputSchema)
  .output(Sd849ReversalResultSchema)
  .route({
    method: "POST",
    summary: "Unlink an email from an 849 claim",
    description:
      "Detaches a supplier email from a claim with a required reason, reversing the line decisions it made; may close the response.",
  });

// ----------------------------------------
// The claim's record page (#7654)
// ----------------------------------------
const ClaimRecordSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("email"), inboundEmailId: UUIDSchema }),
  z.object({ kind: z.literal("person"), userId: UUIDSchema }),
  z.object({ kind: z.literal("none") }),
]);
export type ClaimRecordSource = z.infer<typeof ClaimRecordSourceSchema>;

const ClaimRecordDecisionSchema = z.object({
  status: z.string(),
  acceptedAmount: z.string().nullable(),
});

const ClaimRecordHistoryEntrySchema = z.object({
  activityId: UUIDSchema,
  at: z.string(),
  /** claim-link / stated-decision / repoint wrote the decision; undone / unlinked put it back. */
  action: z.enum([
    "claim-link",
    "stated-decision",
    "repoint",
    "undone",
    "unlinked",
  ]),
  from: ClaimRecordDecisionSchema,
  to: ClaimRecordDecisionSchema,
  /** Who determined the decision written (a reversal: who reversed it). */
  source: ClaimRecordSourceSchema,
  /** A later undo or unlink took this correction back. */
  reversed: z.boolean(),
});
export type ClaimRecordHistoryEntry = z.infer<
  typeof ClaimRecordHistoryEntrySchema
>;

const ClaimRecordLineSchema = z.object({
  sdE844dId: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  productCode: z.string().nullable(),
  claimedAmount: z.string(),
  status: z.string(),
  acceptedAmount: z.string().nullable(),
  /** The email or person that determined the line's current decision. */
  source: ClaimRecordSourceSchema,
  determinedAt: z.string().nullable(),
  history: z.array(ClaimRecordHistoryEntrySchema),
});
export type ClaimRecordLine = z.infer<typeof ClaimRecordLineSchema>;

const ClaimRecordUnplacedRowSchema = z.object({
  /** The row's position in the response's rows — what a re-point names. */
  lineIndex: z.int().nonnegative(),
  /**
   * The row's content fingerprint. A re-point sends it back with `lineIndex`
   * and is refused when the row at that position no longer carries it.
   */
  rowKey: z.string().min(1),
  /** A stored reason, or the old matcher's unmatched row (SD-4), display only. */
  reason: z.enum(SD_849_RECORD_UNPLACED_REASON_VALUES),
  sourceInboundEmailId: UUIDSchema.nullable(),
  invoiceNumber: z.string().nullable(),
  productCode: z.string().nullable(),
  claimedAmount: z.string().nullable(),
  creditedAmount: z.string().nullable(),
  /** The claim lines the row may be re-pointed onto. */
  targetSdE844dIds: z.array(UUIDSchema),
  /**
   * `lump_row_not_paid_as_claimed` (SD-87): the claim lines the row answers
   * together, each with what we claimed for it.
   */
  coversLines: z
    .array(z.object({ sdE844dId: UUIDSchema, claimed: z.string() }))
    .optional(),
});
export type ClaimRecordUnplacedRow = z.infer<
  typeof ClaimRecordUnplacedRowSchema
>;

const ClaimRecordTotalsSchema = z.object({
  claimed: z.string(),
  credited: z.string(),
  denied: z.string(),
});

/**
 * An email's checksum as its claim link recorded it when it applied the email
 * (`at`). It is never recomputed from the claim's current lines.
 */
export const ClaimRecordEmailChecksumSchema = z.object({
  inboundEmailId: UUIDSchema,
  /** The claim-link correction that took it. */
  activityId: UUIDSchema,
  /** When it was taken: the email was applied to the claim. */
  at: z.string(),
  rowCount: z.int().nonnegative(),
  placedCount: z.int().nonnegative(),
  unplacedCount: z.int().nonnegative(),
  status: z.enum(["balanced", "mismatch", "no_report"]),
  /** The email's report: every readable row, placed or not. */
  report: ClaimRecordTotalsSchema,
  /** The claim lines this email decided. */
  applied: ClaimRecordTotalsSchema,
  tolerance: z.string(),
});
export type ClaimRecordEmailChecksum = z.infer<
  typeof ClaimRecordEmailChecksumSchema
>;

const ClaimRecordStatedTotalsSchema = z.object({
  claimed: z.string(),
  credited: z.string().nullable(),
  denied: z.string().nullable(),
});

export const ClaimRecordStatementSchema = z.object({
  activityId: UUIDSchema,
  at: z.string(),
  userId: UUIDSchema.nullable(),
  intent: z.string(),
  reason: z.string().nullable(),
  /** `no_supplier_figures` (#7651): nothing the supplier sent could confirm it. */
  status: z.enum(["balanced", "mismatch", "no_supplier_figures"]),
  stated: ClaimRecordStatedTotalsSchema,
  email: ClaimRecordStatedTotalsSchema,
  reversed: z.boolean(),
});
export type ClaimRecordStatement = z.infer<typeof ClaimRecordStatementSchema>;

/**
 * A claim's 849 response read line by line: each line's decision, the email or
 * person that determined it and its history; the rows the engine could not
 * place, with their reason codes; and the checksum outcomes. Only a per-claim
 * email record (ADR 0005) has this shape — anything else is `applicable: false`.
 */
export const Sd849ClaimRecordSchema = z.discriminatedUnion("applicable", [
  z.object({ applicable: z.literal(false) }),
  z.object({
    applicable: z.literal(true),
    responseId: UUIDSchema,
    submissionId: UUIDSchema,
    lines: z.array(ClaimRecordLineSchema),
    unplaced: z.array(ClaimRecordUnplacedRowSchema),
    checksums: z.array(ClaimRecordEmailChecksumSchema),
    /**
     * Emails whose standing claim link was made before checksums were stored
     * (#7654): there is no checksum to show, and the page says so.
     */
    linksWithoutChecksum: z.array(
      ClaimRecordEmailChecksumSchema.pick({
        inboundEmailId: true,
        activityId: true,
        at: true,
      }),
    ),
    statements: z.array(ClaimRecordStatementSchema),
    emails: z.array(
      z.object({
        id: UUIDSchema,
        subject: z.string().nullable(),
        from: z.string().nullable(),
        receivedAt: z.string().nullable(),
      }),
    ),
    people: z.array(z.object({ id: UUIDSchema, name: z.string() })),
  }),
]);
export type Sd849ClaimRecord = z.infer<typeof Sd849ClaimRecordSchema>;

export const getSd849ClaimRecordContract = oc
  .input(z.object({ sd849ResponseId: UUIDSchema }))
  .output(Sd849ClaimRecordSchema)
  .route({
    method: "GET",
    summary: "Get 849 claim record by line",
    description:
      "Per-line claim record: each line's decision, source and history, unplaced email rows with reasons, checksum results, statements, emails and people.",
  });

/**
 * Re-point ONE unplaced row onto a line of the claim (#7654) — applied through
 * the correction verb, decided by the shared disposition rule with the row's
 * email as the line's source. Refused, with nothing written, when the rule or
 * the line-decision fold would not let the row decide that line.
 */
export const RepointSd849UnplacedRowInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndex: z.int().nonnegative(),
  /** The `rowKey` the claim's record showed for the row at `lineIndex`. */
  rowKey: z.string().min(1),
  sdE844dId: UUIDSchema,
});

export const repointSd849UnplacedRowContract = oc
  .input(RepointSd849UnplacedRowInputSchema)
  .output(z.object({ summary: z.string() }))
  .route({
    method: "POST",
    summary: "Place an unplaced 849 row on a line",
    description:
      "Applies one unplaced supplier email row to a claim line as a correction, deciding that line; refused with no change if rules disallow.",
  });

// ----------------------------------------
// Contract router for edi849Responses
// ----------------------------------------
export const edi849ResponsesContract = {
  list: listEDI849ResponsesContract,
  totals: edi849ResponseTotalsContract,
  get: getEDI849ResponseContract,
  delete: deleteEDI849ResponseContract,
  updateSupplier: updateEDI849ResponseSupplierContract,
  setStatus: setEDI849ResponseStatusContract,
  setDivision: setEDI849ResponseDivisionContract,
  bulkAssignSupplier: bulkAssignSupplierContract,
  downloadExcel: download849ResponsesExcelContract,
  rerunMatching: rerunMatchingContract,
  triggerInvestigation: triggerInvestigationContract,
  saveInvestigationFeedback: saveInvestigationFeedbackContract,
  getResubmissionCandidates: getResubmissionCandidatesContract,
  checkResubmissionData: checkResubmissionDataContract,
  resubmitRejected: resubmitRejectedContract,
  listVdmLinkTargets: listVdmLinkTargetsContract,
  linkVdmToTransactions: linkVdmToTransactionsContract,
  unlinkVdmFromTransactions: unlinkVdmFromTransactionsContract,
  listLineDecisions: listLineDecisionsContract,
  recordLineDecision: recordLineDecisionContract,
  clearLineDecisions: clearLineDecisionsContract,
  listSDCompanies: listSDCompaniesContract,
  listActivities: listSD849ResponseActivitiesContract,
  addComment: addSD849ResponseCommentContract,
  editComment: editSD849ResponseCommentContract,
  deleteComment: deleteSD849ResponseCommentContract,
  undoCorrection: undoSd849CorrectionContract,
  undoClaimLink: undoSd849ClaimLinkContract,
  unlinkEmail: unlinkSd849EmailContract,
  getClaimRecord: getSd849ClaimRecordContract,
  repointUnplacedRow: repointSd849UnplacedRowContract,
};

// ----------------------------------------
// Contract router for edi849UserResponses
// ----------------------------------------
export const edi849UserResponsesContract = {
  getOrCreate: getOrCreateUserResponseContract,
  saveLineDecision: saveLineDecisionContract,
  updateEmailBody: updateEmailBodyContract,
  getProofUploadUrl: getProofUploadUrlContract,
  confirmProofUpload: confirmProofUploadContract,
  removeProofAttachment: removeProofAttachmentContract,
  generateResponseExcel: generateResponseExcelContract,
  sendToSupplier: sendResponseToSupplierContract,
};
